// helpers/TokenHelpers.js

/**
 * Returns the default project token for the given account.
 *
 * If a token ends with an asterisk (*), it is considered default,
 * and the asterisk is removed before returning. If no token is marked
 * as default, the function returns the first token from the list.
 *
 * @param {Object} tokens - The object containing accounts mapped to project tokens.
 * @param {string} accountToken - The account token for which to retrieve the default project token.
 * @returns {string|null} The default project token (without the asterisk) or null if none found.
 */
export const getProjectToken = (tokens, accountToken) => {
  if (!tokens) return null;

  const tokensForAccount = tokens[accountToken];
  if (!tokensForAccount || tokensForAccount.length === 0) return null;

  // Look for a token ending with an asterisk
  const defaultToken = tokensForAccount.find(token => token.endsWith('*'));
  if (defaultToken) {
    return defaultToken.slice(0, -1); // Remove the asterisk
  }

  // Fallback: return the first token in the array
  return tokensForAccount[0];
};

/**
 * Parses the projectAdmin string and returns:
 * - tokens: an object mapping account tokens to arrays of project tokens.
 * - default: an object with the default account token and default project token.
 *
 * The default account is denoted by an asterisk (*) appended to the account token.
 * The default project token is denoted by an asterisk (*) appended to it.
 *
 * @param {string} projectAdminString - The input string from the model.
 * @returns {{
 *   tokens: Record<string, string[]>,
 *   default: { accountToken: string|null, projectToken: string|null }
 * }}
 */
export const parseProjectAdmin = (projectAdminString) => {
  const tokens = {};
  let defaultAccount = null;
  let defaultProject = null;
  // Split the string by semicolons, filtering out empty parts.
  const accountEntries = projectAdminString.split(';').filter(Boolean);

  for (const entry of accountEntries) {
    const [account, projectsStr] = entry.split(':');
    if (!account) continue;

    const isDefaultAccount = account.endsWith('*');
    const cleanAccount = isDefaultAccount ? account.slice(0, -1) : account;

    // Parse project tokens
    const projectTokens = projectsStr
      ? projectsStr.split(',').map(token => token.trim())
      : [];

    // Determine default project if the account is default
    if (isDefaultAccount) {
      defaultAccount = cleanAccount;
      const defaultProjectToken = projectTokens.find(token => token.endsWith('*'));
      if (defaultProjectToken) {
        defaultProject = defaultProjectToken.slice(0, -1);
      }
    }

    // Store in tokens object, preserving * on default project tokens
    tokens[cleanAccount] = projectTokens;
  }

  return {
    tokens,
    default: {
      accountToken: defaultAccount,
      projectToken: defaultProject
    }
  };
};

export const getDefaultFormToken = (projectAdminTokens, currentProjectToken) => {
  if (!projectAdminTokens || !Array.isArray(projectAdminTokens)) {
      console.error("Invalid projectAdminTokens data.");
      return null;
  }

  const tokenObj = projectAdminTokens.find(tokenObj => tokenObj.projectToken === currentProjectToken);

  if (tokenObj) {
      return tokenObj.defaultFormToken;
  } else {
      console.error(`No matching projectToken found for: ${currentProjectToken}`);
      return null;
  }
};
